<template>
  <v-row v-if="items.length > 0" dense class="video-cards-grid">
    <v-col v-for="item in items" :key="item.id" cols="12" sm="6" md="4" lg="3">
      <v-card
        :class="['video-card', { 'video-card--selected': isSelected(item) }]"
        outlined
        @click="$emit('open', item)"
        @mouseenter="onCardHover(item.id, true)"
        @mouseleave="onCardHover(item.id, false)"
      >
        <div class="video-preview">
          <v-checkbox
            :input-value="isSelected(item)"
            hide-details
            dense
            color="primary"
            class="video-select-checkbox"
            @click.stop
            @change="$emit('toggle-select', { item, checked: $event })"
          />
          <img
            v-if="item.preview_image_url"
            class="video-preview-media"
            :src="item.preview_image_url"
            :alt="`Preview of ${displayName(item)}`"
            loading="lazy"
          />
          <video
            v-else-if="item.preview_video_url"
            :ref="`previewVideo-${item.id}`"
            class="video-preview-media"
            :src="item.preview_video_url"
            preload="metadata"
            loop
            muted
            playsinline
          />
          <div v-if="item.preview_video_url || item.preview_image_url" class="video-preview-overlay" />
          <div v-if="hasVideoDuration(item)" class="video-preview-duration">
            {{ formatVideoDuration(item) }}
          </div>
          <v-tooltip v-if="item.incoherence_label" top>
            <template #activator="{ on, attrs }">
              <div
                class="video-preview-label-badge"
                :class="
                  isTruthfulLabel(item)
                    ? 'video-preview-label-badge--truthful'
                    : 'video-preview-label-badge--incoherence'
                "
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="13" color="white">
                  {{ isTruthfulLabel(item) ? 'mdi-check-decagram' : 'mdi-drama-masks' }}
                </v-icon>
              </div>
            </template>
            <span>{{ isTruthfulLabel(item) ? 'Labeled: truthful' : 'Labeled: lie' }}</span>
          </v-tooltip>
          <div v-if="!item.preview_video_url" class="video-preview-placeholder">
            <v-icon size="44" color="white">mdi-file-video</v-icon>
          </div>
        </div>

        <v-card-text class="pb-2">
          <div class="subtitle-2 font-weight-medium text-truncate">{{ displayName(item) }}</div>
          <div class="caption grey--text text--darken-1">{{ item.created_at | formatDate }}</div>
          <div class="d-flex align-center mt-1" style="gap: 6px">
            <v-chip x-small :color="statusColor(item.status)">{{ statusText(item.status) }}</v-chip>
            <v-chip
              v-if="queueChipFor(item)"
              x-small
              outlined
              :color="queueChipFor(item).color"
              class="manual-queue-chip"
              :title="queueChipFor(item).title"
            >
              <v-icon x-small left>{{ queueChipFor(item).icon }}</v-icon>
              {{ queueChipFor(item).text }}
            </v-chip>
            <span class="caption grey--text text--darken-1">{{ formatAnalysisDuration(item) }}</span>
            <v-spacer />
            <v-tooltip v-if="item.status === 'COMPLETED'" top>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  x-small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="$emit('typology', item)"
                >
                  <v-icon small>mdi-counter</v-icon>
                </v-btn>
              </template>
              <span>Gestures by typology</span>
            </v-tooltip>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <v-card v-else flat class="pa-6 text-center">
    <v-icon size="36" color="grey">mdi-file-video-outline</v-icon>
    <p class="mt-2 mb-0 grey--text text--darken-1">No videos found on this page.</p>
  </v-card>
</template>

<script>
import featureFlags from '@/config/featureFlags';

/**
 * Cards mode of the manual video list (extracted from ManualVideoAnalysisList in M4 F13, same
 * markup and behaviour): preview with hover playback, label badge, duration, SQL status chip and
 * the live queue chip ('Running 40%' / 'Queued #3') from the analysisQueue store.
 *
 * Emits: `open` (item), `toggle-select` `{ item, checked }`, `typology` (item).
 */
export default {
  name: 'VideoCardsGrid',

  props: {
    items: { type: Array, default: () => [] },
    /** Ids of the selected videos. */
    selectedIds: { type: Array, default: () => [] },
    sourceType: { type: String, default: 'manual_analysis' },
  },

  computed: {
    selectedIdSet() {
      return new Set(this.selectedIds.map((id) => Number(id)));
    },
  },

  methods: {
    isSelected(item) {
      return this.selectedIdSet.has(Number(item?.id));
    },
    displayName(item) {
      return item?.name || item?.original_filename || `Analysis ${item?.id}`;
    },
    getPreviewVideoElement(analysisId) {
      const ref = this.$refs[`previewVideo-${analysisId}`];
      if (!ref) return null;
      return Array.isArray(ref) ? ref[0] : ref;
    },
    onCardHover(analysisId, isHovering) {
      const video = this.getPreviewVideoElement(analysisId);
      if (!video) return;
      if (isHovering) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        return;
      }
      video.pause();
      try {
        video.currentTime = 0;
      } catch (error) {
        // Ignore seek errors on unload/not-ready states.
      }
    },
    /**
     * Queue chip for a card (M3 F11): 'Running 40%' / 'Queued #3' from the live queue lanes;
     * null when the flag is off, the store is absent or the video is idle.
     */
    queueChipFor(item) {
      if (!featureFlags.analysisQueue || !item || !this.$store) return null;
      const runsForSource = this.$store.getters['analysisQueue/runsForSource'];
      const queueIndexOf = this.$store.getters['analysisQueue/queueIndexOf'];
      if (typeof runsForSource !== 'function') return null;
      const runs = runsForSource(this.sourceType, item.id);
      if (!runs.length) return null;
      const running = runs.find((run) =>
        ['DISPATCHED', 'RUNNING', 'CANCEL_REQUESTED'].includes(String(run.status || '').toUpperCase())
      );
      if (running) {
        const status = String(running.status || '').toUpperCase();
        if (status === 'CANCEL_REQUESTED') {
          return { text: 'Cancelling', color: 'warning', icon: 'mdi-cancel', title: 'Cancel requested' };
        }
        const pct = Math.max(0, Math.min(100, Math.round(Number(running.progress) || 0)));
        return {
          text: `Running ${pct}%`,
          color: 'primary',
          icon: 'mdi-progress-clock',
          title: running.current_step ? `Stage: ${running.current_step}` : 'Running',
        };
      }
      const queued = runs.find((run) => String(run.status || '').toUpperCase() === 'QUEUED');
      if (!queued) return null;
      const index =
        typeof queueIndexOf === 'function' ? queueIndexOf(queued._id || queued.run_id) : null;
      return {
        text: index ? `Queued #${index}` : 'Queued',
        color: 'grey darken-1',
        icon: 'mdi-timer-sand',
        title: queued.batch ? `Batch ${queued.batch}` : 'Waiting in the analysis queue',
      };
    },
    statusColor(status) {
      if (status === 'COMPLETED') return 'success';
      if (status === 'ANALYSING') return 'primary';
      if (status === 'UPLOADING') return 'info';
      if (status === 'ERROR') return 'error';
      return 'grey';
    },
    statusText(status) {
      if (status === 'COMPLETED') return 'Completed';
      if (status === 'ANALYSING') return 'Analysing...';
      if (status === 'UPLOADING') return 'Uploading...';
      if (status === 'ERROR') return 'Error';
      return status;
    },
    formatAnalysisDuration(item) {
      const started = item?.analysis_started_at ? new Date(item.analysis_started_at).getTime() : null;
      const completed = item?.analysis_completed_at
        ? new Date(item.analysis_completed_at).getTime()
        : null;
      if (!started || !completed || completed <= started) return '-';
      return this.formatMinutesSeconds(Math.round((completed - started) / 1000));
    },
    getVideoDurationSeconds(item) {
      const candidates = [
        item?.video_duration_seconds,
        item?.videoDurationSeconds,
        item?.duration_seconds,
        item?.durationSeconds,
        item?.video_duration,
        item?.videoDuration,
        item?.duration,
      ];
      for (const candidate of candidates) {
        const value = Number(candidate);
        if (Number.isFinite(value) && value > 0) return value;
      }
      return null;
    },
    hasVideoDuration(item) {
      const seconds = this.getVideoDurationSeconds(item);
      return Number.isFinite(seconds) && seconds > 0;
    },
    formatVideoDuration(item) {
      const seconds = this.getVideoDurationSeconds(item);
      if (!Number.isFinite(seconds) || seconds <= 0) return '--:--';
      return this.formatMinutesSeconds(seconds);
    },
    isTruthfulLabel(item) {
      return item?.incoherence_label?.label_type === 'truthful';
    },
    formatMinutesSeconds(totalSeconds) {
      const safeSeconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
      const minutes = Math.floor(safeSeconds / 60);
      const seconds = safeSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },
  },
};
</script>

<style scoped>
.video-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  box-shadow: none !important;
}

.video-card--selected {
  border-color: #6200ea !important;
  background: rgba(98, 0, 234, 0.08) !important;
}

.video-preview {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #e5e7eb;
  overflow: hidden;
}

.video-preview-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.video-preview-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.42);
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.video-card:hover .video-preview-overlay {
  opacity: 0;
}

.video-preview-duration {
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 2;
  color: #fff;
  font-size: 11px;
  line-height: 1;
  background: rgba(0, 0, 0, 0.72);
  border-radius: 4px;
  padding: 4px 6px;
  pointer-events: none;
}

/* Lie/truthful label badge (top-right, same chip language as the duration badge). */
.video-preview-label-badge {
  position: absolute;
  right: 8px;
  top: 8px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 4px 6px;
  line-height: 1;
}

.video-preview-label-badge--incoherence {
  background: rgba(198, 40, 40, 0.85);
}

.video-preview-label-badge--truthful {
  background: rgba(46, 125, 50, 0.85);
}

.video-preview-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #2d2d2d 0%, #1d1d1d 100%);
}

.video-select-checkbox {
  position: absolute;
  left: 8px;
  top: 8px;
  z-index: 2;
  margin: 0;
  background: transparent;
  border-radius: 0;
  padding: 0;
}

@media (max-width: 600px) {
  .video-preview-duration {
    font-size: 10px;
    padding: 3px 5px;
  }

  .video-preview-label-badge {
    padding: 3px 5px;
  }
}
</style>
